import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StudentCards from "./UserProfileCard";
import StudentService from "./Locality/Service/studentService";
import PageTitle from "../../components/PageTitle/PageTitle";

const useStyles = makeStyles((theme) => ({
  page: { paddingBottom: 40 },
  card: {
    borderRadius: 18,
    border: "1px solid #edf1f6",
    boxShadow: "0 10px 28px rgba(15, 43, 74, 0.06)",
    overflow: "hidden",
    background: "#fff",
  },
  monthHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #f1f5f9",
  },
  monthTitle: { fontWeight: 800, color: "#334155", fontSize: "1.05rem" },
  session: { fontWeight: 700, color: "#64748b", fontSize: "0.95rem" },
  filters: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  dayRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: "1px solid #f8fafc",
  },
  dayLabel: { color: "#475569", fontWeight: 600, fontSize: "0.98rem" },
  markP: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#0d9488",
    color: "#fff",
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.95rem",
  },
  markA: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#dc2626",
    color: "#fff",
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.95rem",
  },
  markDash: {
    color: "#ea580c",
    fontWeight: 800,
    fontSize: "1.05rem",
    minWidth: 36,
    textAlign: "center",
  },
  statusHint: {
    display: "block",
    fontSize: "0.75rem",
    color: "#94a3b8",
    fontWeight: 600,
    marginTop: 2,
  },
  todayChip: {
    marginLeft: 8,
    fontSize: "0.7rem",
    fontWeight: 800,
    color: "#1769d6",
    background: "#e8f0fe",
    borderRadius: 8,
    padding: "2px 8px",
  },
  empty: { padding: 32, textAlign: "center", color: "#94a3b8" },
  monthlyCard: {
    marginTop: 20,
    borderRadius: 16,
    border: "1px solid #edf1f6",
    overflow: "hidden",
  },
  monthlyTitle: {
    fontWeight: 800,
    color: "#0F3B62",
    padding: "14px 18px",
    background: "#f8fafc",
    borderBottom: "1px solid #e8eef4",
  },
}));

function getUserDetails() {
  try {
    return JSON.parse(localStorage.getItem("userDetail") || "{}") || {};
  } catch {
    return {};
  }
}

export default function ParentAttendance() {
  const classes = useStyles();
  const userDetails = useMemo(() => getUserDetails(), []);
  const [studentList, setStudentList] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [monthly, setMonthly] = useState([]);

  const schooleId = useMemo(() => {
    const id = userDetails?.schooleId || userDetails?.schoolId;
    return id && typeof id === "object" ? id?._id : id;
  }, [userDetails]);

  const currentMonth = Number(calendar?.currentMonth) || 0;
  const currentYear = Number(calendar?.currentYear) || Number(calendar?.year) || 0;

  const monthOptions = useMemo(() => {
    const lastMonth = currentMonth || 12;
    return Array.from({ length: lastMonth }, (_, i) => ({
      value: i + 1,
      label: new Date(2000, i, 1).toLocaleString(undefined, { month: "long" }),
    }));
  }, [currentMonth]);

  useEffect(() => {
    const body = {};
    if (userDetails.role === "PARENT") body.userId = userDetails._id;
    else body.schooleId = schooleId;
    StudentService.findStudentList(body)
      .then((res) => {
        const list = (Array.isArray(res) ? res : []).map((s) => ({
          ...s,
          fullName: `${s.firstName || ""} ${s.lastName || ""}`.trim(),
        }));
        setStudentList(list);
        if (list[0]?._id) setStudentId(list[0]._id);
      })
      .catch(() => setStudentList([]));
  }, [userDetails._id, userDetails.role, schooleId]);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    const body = { schooleId };
    if (month != null) body.month = month;
    StudentService.getStudentAttendanceSummary(studentId, body)
      .then((res) => {
        if (cancelled) return;
        setMonthly(Array.isArray(res?.data) ? res.data : []);
        setCalendar(res?.calendar || null);
      })
      .catch(() => {
        if (cancelled) return;
        setMonthly([]);
        setCalendar(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, schooleId, month]);

  const visibleDays = useMemo(
    () => (calendar?.days || []).filter((d) => !d.isFuture),
    [calendar],
  );

  const monthLabel = (y, m) =>
    new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, {
      month: "long",
      year: "numeric",
    });

  const selectedMonth = month != null ? month : Number(calendar?.month) || 1;
  const selectedYear = currentYear || Number(calendar?.year) || 0;

  const monthRow = useMemo(
    () =>
      (Array.isArray(monthly) ? monthly : []).find(
        (row) =>
          Number(row?._id?.year) === Number(selectedYear) &&
          Number(row?._id?.month) === Number(selectedMonth),
      ) || null,
    [monthly, selectedYear, selectedMonth],
  );

  return (
    <Container maxWidth="md" className={classes.page}>
      <PageTitle title="Attendance" />
      {studentList.length > 1 ? (
        <Box mb={2}>
          <StudentCards
            studentList={studentList}
            onStudentSelect={(id) => setStudentId(id)}
            selectedStudentId={studentId}
          />
        </Box>
      ) : null}

      <Box className={classes.filters}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select
            label="Month"
            value={selectedMonth}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {monthOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Card className={classes.card} style={{ maxWidth: 600 }}>
        <Box className={classes.monthHead}>
          <Typography className={classes.monthTitle}>
            {calendar?.monthLabel || "Attendance details"}
          </Typography>
          <Typography className={classes.session}>{calendar?.session || "Attendance Status"}</Typography>
        </Box>
        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} />
          </Box>
        ) : visibleDays.length === 0 ? (
          <Typography className={classes.empty}>No attendance days for this month.</Typography>
        ) : (
          visibleDays.map((day) => (
            <Box key={day.date} className={classes.dayRow}>
              <Box>
                <Typography className={classes.dayLabel}>
                  {day.displayDate}
                  {day.isToday ? <span className={classes.todayChip}>Today</span> : null}
                </Typography>
              </Box>
              {day.type === "present" || day.present ? (
                <span className={classes.markP}>P</span>
              ) : day.type === "absent" || day.absent ? (
                <span className={classes.markA}>A</span>
              ) : (
                <span className={classes.markDash}>___</span>
              )}
            </Box>
          ))
        )}
      </Card>

      <Card className={classes.monthlyCard}>
        <Typography className={classes.monthlyTitle}>Monthly summary</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Present</TableCell>
              <TableCell>Absent</TableCell>
              <TableCell>Total marked</TableCell>
              <TableCell>Present %</TableCell>
              <TableCell>Absent %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={{ fontWeight: 700 }}>
                {monthLabel(selectedYear, selectedMonth)}
              </TableCell>
              <TableCell style={{ color: "#15803d", fontWeight: 700 }}>
                {monthRow?.presentCount ?? 0}
              </TableCell>
              <TableCell style={{ color: "#dc2626", fontWeight: 700 }}>
                {monthRow?.absentCount ?? 0}
              </TableCell>
              <TableCell>{monthRow?.totalCount ?? 0}</TableCell>
              <TableCell>{monthRow?.presentPercentage ?? 0}%</TableCell>
              <TableCell>{monthRow?.absentPercentage ?? 0}%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    </Container>
  );
}
